"use client";

import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Hover/focus tooltip for phase help text. Rendered through a portal with fixed
// positioning so it escapes the Kanban columns' `overflow-hidden` and the board's
// horizontal scroll (otherwise the bullet list gets clipped/truncated). Position
// is clamped to the viewport so it is always fully visible.

type PhaseHelpTooltipProps = {
    title: string;
    /** Optional prose, rendered above the bullets. Some help reads as a
     *  sentence rather than a list — the admissions checklist's per-question
     *  help in particular explains what a question means before it enumerates
     *  what counts. */
    body?: string;
    items?: string[];
    children: ReactNode;
    align?: "left" | "center" | "right";
    className?: string;
};

const PANEL_WIDTH = 264;
const MARGIN = 12;

export function PhaseHelpTooltip({
    title,
    body,
    items,
    children,
    align = "left",
    className = "",
}: PhaseHelpTooltipProps) {
    const triggerRef = useRef<HTMLSpanElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const reposition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        let left: number;
        if (align === "center") left = r.left + r.width / 2 - PANEL_WIDTH / 2;
        else if (align === "right") left = r.right - PANEL_WIDTH;
        else left = r.left;
        // Clamp horizontally to the viewport.
        const maxLeft = window.innerWidth - PANEL_WIDTH - MARGIN;
        left = Math.max(MARGIN, Math.min(left, maxLeft));
        // Below the trigger by default; flipped above when the panel would run
        // past the bottom of the viewport. Some bodies are a full paragraph (the
        // admissions Accountability definition), so a tall panel opened near the
        // foot of the page would otherwise be cut off with no way to scroll it —
        // the panel is `pointer-events-none` and lives outside the page flow.
        const height = panelRef.current?.offsetHeight ?? 0;
        let top = r.bottom + 8;
        if (height && top + height + MARGIN > window.innerHeight) {
            const above = r.top - height - 8;
            top = above >= MARGIN ? above : Math.max(MARGIN, window.innerHeight - height - MARGIN);
        }
        setCoords({ top, left });
    }, [align]);

    const show = useCallback(() => {
        reposition();
        setOpen(true);
    }, [reposition]);

    const hide = useCallback(() => setOpen(false), []);

    // The first `reposition` runs before the panel is in the DOM, so it has no
    // height to flip against — measure once it has mounted and settle it then.
    useLayoutEffect(() => {
        if (open) reposition();
    }, [open, reposition]);

    useEffect(() => {
        if (!open) return;
        const handler = () => reposition();
        window.addEventListener("scroll", handler, true);
        window.addEventListener("resize", handler);
        return () => {
            window.removeEventListener("scroll", handler, true);
            window.removeEventListener("resize", handler);
        };
    }, [open, reposition]);

    return (
        <span
            ref={triggerRef}
            className={`relative inline-flex ${className}`}
            tabIndex={0}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            {children}
            {open &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={panelRef}
                        role="tooltip"
                        style={{ position: "fixed", top: coords.top, left: coords.left, width: PANEL_WIDTH }}
                        className="z-[9999] rounded-xl border border-[#e2e8f0] bg-white p-3 text-left shadow-lg pointer-events-none"
                    >
                        <span className="block text-[10px] font-black uppercase tracking-widest text-[#003d45] mb-1.5">
                            {title}
                        </span>
                        {body && (
                            <p className="text-[11px] leading-snug text-[#475569]">{body}</p>
                        )}
                        {items && items.length > 0 && (
                            <ul className={`space-y-1 ${body ? "mt-1.5" : ""}`}>
                                {items.map((it, i) => (
                                    <li key={i} className="flex gap-1.5 text-[11px] leading-snug text-[#475569]">
                                        <span className="text-[#007a8a] flex-shrink-0">•</span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>,
                    document.body,
                )}
        </span>
    );
}
